import { AppDispatch } from '@/app/store';
import { DraftsList } from '@/components/home/drafts/DraftsList';
import { DraftsTable } from '@/components/home/drafts/DraftsTable';
import { ErrorDTO } from '@/types/ErrorDTO';
import { Grid, Pagination, useMediaQuery } from '@mui/material';
import { LetterDraftDTO } from '@/types/letterDraft/LetterDraftDTO';
import { LetterDraftFileDTO } from '@/types/letterDraft/LetterDraftFileDTO';
import { LetterDraftIdDTO } from '@/types/letterDraft/LetterDraftIdDTO';
import { LetterDraftListItemDTO } from '@/types/letterDraft/LetterDraftListItemDTO';
import { LetterDraftRequestDTO } from '@/types/letterDraft/LetterDraftRequestDTO';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { OutlinedField } from '@/components/common/field/OutlinedField';
import { PhotoStageDTO } from '@/types/create/PhotoStageDTO';
import { ROUTE_CREATE_LETTER_AUTHOR, ROUTE_CREATE_LETTER_CLIENT, ROUTE_CREATE_REPLY_LETTER } from '@/app/routes';
import { SortRequestDTO } from '@/types/page/SortRequestDTO';
import { StagedPhotoDTO } from '@/types/create/StagedPhotoDTO';
import { TOO_MANY_REQUESTS } from '@/constants/HttpStatus';
import { Theme } from '@mui/material/styles';
import { UserDTO } from '@/types/user/UserDTO';
import {
  clearLetterDraftId,
  setAnswerOrderedForm,
  setAnswersCountForm,
  setAuthor,
  setLetterDraftId,
  setLetterTextForm,
  setRegionAndDepartment,
  setSender,
  setStagedPhotos,
} from '@/services/createFormSlice';
import { clearLetterSbpState } from '@/services/sbpSlice';
import { formatDraftPhotoLimitMessage, formatLostDraftPhotosMessage } from '@/utils/string-utils';
import { getCurrentUser } from '@/services/authSlice';
import {
  getLetterDraftRequest,
  setLetterDraftRequestPageNumber,
  setLetterDraftRequestPageSize,
  setLetterDraftSearchRequestAddressee,
  setLetterDraftSortRequest,
} from '@/services/letter/LetterDraftSlice';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useClearCreateForm } from '@/app/hooks/useClearCreateForm';
import { useDebounce } from '@/app/hooks/useDebounce';
import { useGetLetterDraftMutation, useGetLetterDraftsQuery } from '@/services/api/letterDraftApiSlice';
import { useSnackbar } from 'notistack';
import { useStagePhotoMutation } from '@/services/api/letterApiSlice';
import { validateAuthor, validateSender } from '@/utils/validation-utils';
import React, { ChangeEvent, FC, ReactElement, useEffect, useState } from 'react';

/**
 * What restoring one photo of a draft came back as: the staged photo, a refusal for the staging limit,
 * or neither when the photo itself could not be read back.
 * <p>
 * A refusal is kept apart from a lost photo because the two need opposite advice.
 * A lost photo is attached again; a photo refused for the limit cannot be, until the sender frees room.
 * The two fields are separate because a refusal does not always carry wording to pass on —
 * one answered by the proxy rather than by the application has none — and it still has to stop the restore.
 */
interface DraftPhotoRestore {
  photo?: StagedPhotoDTO;
  limitReached?: boolean;
  limitReason?: string;
}

export const DraftsBlock: FC = (): ReactElement => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate: NavigateFunction = useNavigate();
  const clearCreateForm: () => Promise<void> = useClearCreateForm();
  const { enqueueSnackbar } = useSnackbar();

  const request: LetterDraftRequestDTO = useAppSelector(getLetterDraftRequest);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const { data, isFetching, isLoading } = useGetLetterDraftsQuery({ ...request }, { skip: !authUser });
  const [getLetterDraft] = useGetLetterDraftMutation();
  const [stagePhoto] = useStagePhotoMutation();

  const [search, setSearch] = useState<string>('');

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  const handlePage = (page: number): void => {
    dispatch(setLetterDraftRequestPageNumber(page - 1));
  };

  const handleAddressee = useDebounce<string>((addressee: string): void => {
    dispatch(setLetterDraftSearchRequestAddressee(addressee));
    dispatch(setLetterDraftRequestPageNumber(0));
  }, 500);

  const handleSearch = (addressee: string): void => {
    setSearch(addressee);
    handleAddressee(addressee);
  };

  const handleSort = (sortRequest: SortRequestDTO): void => {
    dispatch(setLetterDraftSortRequest(sortRequest));
  };

  const handleDraftClick = async (draft: LetterDraftListItemDTO) => {
    const dto: LetterDraftIdDTO = {
      id: draft.id,
    };
    try {
      const response: LetterDraftDTO = await getLetterDraft(dto).unwrap();
      await handleDraft(response);
      if (!validateSender(response.sender)) {
        navigate(ROUTE_CREATE_LETTER_CLIENT);
      } else if (!validateAuthor(response.author)) {
        navigate(ROUTE_CREATE_LETTER_AUTHOR);
      } else {
        navigate(ROUTE_CREATE_REPLY_LETTER);
      }
    } catch (error) {
      enqueueSnackbar('Ошибка получения черновика', { variant: 'error' });
    }
  };

  /**
   * Turns a photo stored with the draft back into a staged photo, because the create form holds
   * staged identifiers rather than the bytes themselves.
   */
  const stageDraftPhoto = async (photo: LetterDraftFileDTO, departmentId?: number): Promise<DraftPhotoRestore> => {
    const response: Response = await fetch(`data:image/jpeg;base64,${photo.content}`);
    const file: File = new File([await response.blob()], photo.name, { type: 'image/jpeg' });
    return await stagePhoto({ image: file, departmentId: departmentId })
      .unwrap()
      .then(
        (staged: PhotoStageDTO): DraftPhotoRestore => ({
          photo: { id: staged.uuid, thumbnail: staged.thumbnail, stagedAt: Date.now() },
        }),
      )
      .catch((e: { status?: number; data?: ErrorDTO }): DraftPhotoRestore => {
        if (e?.status === TOO_MANY_REQUESTS) {
          // A refusal with no body — one the proxy answered rather than the application — still means stop asking,
          // and is reported as a loss because there is nothing better to say about it.
          return { limitReached: true, limitReason: e.data?.message };
        }
        // The photo is lost from the reopened draft,
        // and the sender would otherwise pay for fewer photos than the draft held without ever being told.
        console.error(`Не удалось восстановить фото «${photo.name}» из черновика.`);
        return {};
      });
  };

  const handleDraft = async (draft: LetterDraftDTO): Promise<void> => {
    const { region, department, author, sender, letter, isAnswerOrdered, answersCount, images } = draft;
    await clearCreateForm();
    dispatch(clearLetterSbpState());
    dispatch(clearLetterDraftId());
    dispatch(setRegionAndDepartment({ region, department }));
    if (author) {
      dispatch(setAuthor(author));
    }
    if (sender) {
      dispatch(setSender(sender));
    }
    if (letter) {
      dispatch(setLetterTextForm(letter));
    }
    dispatch(setAnswerOrderedForm(isAnswerOrdered));
    dispatch(setAnswersCountForm(answersCount || 1));
    const restored: StagedPhotoDTO[] = [];
    let limitReason: string | undefined;
    for (const photo of images || []) {
      const result: DraftPhotoRestore = await stageDraftPhoto(photo, department?.id);
      if (result.limitReached) {
        // Every photo after this one would be refused for the same reason, so stop asking.
        limitReason = result.limitReason;
        break;
      }
      if (result.photo) {
        restored.push(result.photo);
      }
    }
    const lost: number = (images || []).length - restored.length;
    if (lost > 0) {
      enqueueSnackbar(
        limitReason ? formatDraftPhotoLimitMessage(lost, limitReason) : formatLostDraftPhotosMessage(lost),
        { variant: 'error' },
      );
    }
    dispatch(setStagedPhotos(restored));
    dispatch(setLetterDraftId(draft.id));
  };

  useEffect((): void => {
    dispatch(setLetterDraftRequestPageSize(isMobile ? 5 : 8));
  }, [dispatch, isMobile]);

  useEffect((): void => {
    setSearch(request.searchRequest.addressee || '');
  }, [request.searchRequest.addressee]);

  return (
    <Grid container={true} direction={'column'} spacing={2}>
      <Grid item={true}>
        <OutlinedField
          fullWidth={true}
          size={'small'}
          placeholder={'Поиск по адресату'}
          name={'addressee'}
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            handleSearch(e.target.value);
          }}
          onClear={() => {
            handleSearch('');
          }}
        />
      </Grid>
      <Grid item={true}>
        {isMobile ? (
          <DraftsList
            isFetching={isFetching}
            isLoading={isLoading}
            drafts={data?.content || []}
            onDraftClick={handleDraftClick}
          />
        ) : (
          <DraftsTable
            isFetching={isFetching}
            isLoading={isLoading}
            drafts={data?.content || []}
            sortRequest={
              request.pageRequest.sortRequest || {
                properties: ['answer.processedAt', 'answerDate'],
                direction: 'DESC',
              }
            }
            onSort={handleSort}
            onDraftClick={handleDraftClick}
          />
        )}
      </Grid>
      <Grid item={true} display={'flex'} justifyContent={isMobile ? 'center' : 'right'}>
        <Pagination
          count={data?.totalPages || 1}
          page={(request.pageRequest?.pageNumber || 0) + 1}
          onChange={(e: ChangeEvent<unknown>, page: number) => {
            handlePage(page);
          }}
        />
      </Grid>
    </Grid>
  );
};
